import { Injectable } from '@angular/core';
import { Apollo, gql } from 'apollo-angular';
import type { Observable } from 'rxjs';
import { map } from 'rxjs';
import type { ClinicInterface } from 'src/app/shared/models/clinic.interface';
import type {
  AddOrUpdateDtoInterface,
  ClinicFullInterface,
  ClinicMinimalInterface,
  ClinicWithOrthancModalityInterface
} from '../models/admin-clinic.interface';
import { AppStateService } from '../../../shared/services/app-state.service';

// Query pour les cliniques MyPacs : inclut les cliniques natives MyPacs ET les cliniques SCC avec accès MyPacs
// NOTE: take: 10000 nécessaire car le backend utilise UseOffsetPaging() avec DefaultPageSize = 20
export const GetMyPacsClinicsQuery = gql`query adminGetMyPacsClinics {
  clinics(order: { name: ASC }, where: { or: [
    { isMyPacs: { eq: true } },
    { hasMyPacsAccess: { eq: true } }
  ] }, take: 10000) {
    items {
      id
      name
      shortName
      parentName
      orthancIp
      orthancPort
      isGroup
      isMyPacs
      hasMyPacsAccess
      isLite
    }
  }
}`;

// Query pour les cliniques SCC : uniquement les cliniques SCC (isMyPacs = false)
export const GetSccClinicsQuery = gql`query adminGetSccClinics {
  clinics(order: { name: ASC }, where: { isMyPacs: { eq: false } }, take: 10000) {
    items {
      id
      name
      shortName
      parentName
      orthancIp
      orthancPort
      isGroup
      isMyPacs
      hasMyPacsAccess
      isLite
    }
  }
}`;

// Query pour toutes les cliniques (MyPacs + SCC)
export const GetAllClinicsQuery = gql`query adminGetAllClinics {
  clinics(order: { name: ASC }, take: 10000) {
    items {
      id
      name
      shortName
      parentName
      orthancIp
      orthancPort
      isGroup
      isMyPacs
      hasMyPacsAccess
      isLite
    }
  }
}`;

export const GetUserClinicsWithModalityQuery =  gql`query adminGetUserClinics {
  userClinics(order: { name: ASC } ) {
    id
    name
    shortName
    parentName
    orthancIp
    orthancPort
  }
}`;


export const GetClinicByIdQuery =  gql`query admingetClinic($clinicId: Long!) {
  clinicById(clinicId: $clinicId) {
    id
    name
    alias
    address1
    address2
    postalCode
    city
    country
    clinicStatus
    email
    phone
    fax
    site
    facebook
    logoUrl
    parentId
    publicId
    isGroup
    clinicTypeId
    hasPaymentMethod
    studyStorageMode
    maxNbModality
    isLite
    pms
    isMyPacs
    hasMyPacsAccess
  }
}`;


// NOTE: Requesting a very high take value (10000) to effectively remove pagination limit
// See GetAllClinicsWithModalityQuery for explanation
export const GetClinicGroupsQuery = gql` query adminGetClinicGroups {
  clinics(order: { name: ASC }, where: { isGroup: { eq: true} }, take: 10000) {
    items {
      id
      name
      isGroup
    }
  }
}`;

@Injectable()
export class AdminClinicService {
  constructor(private readonly apollo: Apollo, private readonly appStateService: AppStateService) { }

  getStripeClientSecretForClinicPaymentMethod(clinicId: number): Observable<{
    clientSecret: string;
    publishableKey: string;
  }> {
    return this.apollo.query<{ stripeClientSecretForClinicPaymentMethod: {
      clientSecret: string;
      publishableKey: string;
    }; }>({
      query: gql`query getStripeClientSecretForClinicPaymentMethod($clinicId: Long!) {
        stripeClientSecretForClinicPaymentMethod(clinicId : $clinicId) {
          clientSecret
          publishableKey
        }
      }`,
      variables: { clinicId },
      fetchPolicy: 'network-only'
    }).pipe(map(res => res.data.stripeClientSecretForClinicPaymentMethod));
  }

  getStripePaymentMethodForClinic(clinicId: number): Observable<PaymentMethod> {
    return this.apollo.query<{ stripePaymentMethodForClinic: PaymentMethod }>({
      query: gql`query stripePaymentMethodForClinic($clinicId: Long!) {
        stripePaymentMethodForClinic(clinicId : $clinicId) {
          hasMethod
          bankCode
          brand
          expMonth
          expYear
          isCard
          last4
          valid
          stripeCustomerId
          hasUnpaidInvoices
        }
      }`,
      variables: { clinicId }
    }).pipe(map(res => res.data.stripePaymentMethodForClinic));
  }

  getAmountFuturInvoiceForClinic(clinicId: number): Observable<number> {
    return this.apollo.query<{ amountFuturInvoice: number }>({
      query: gql`query amountFuturInvoice($clinicId: Long!) {
        amountFuturInvoice(clinicId : $clinicId)
      }`,
      variables: { clinicId }
    }).pipe(map(res => res.data.amountFuturInvoice));
  }

  getClinicsWithModality(): Observable<ClinicWithOrthancModalityInterface[]> {
    // Utiliser la query appropriée selon le contexte (SCC ou MyPacs)
    const query = this.appStateService.isScc ? GetSccClinicsQuery : GetMyPacsClinicsQuery;

    return this.apollo.query<GetAllClinicsReponse>({
      query,
      fetchPolicy: 'network-only'
    }).pipe(map(res => res.data.clinics?.items ?? []));
  }

  getAllClinics(): Observable<ClinicWithOrthancModalityInterface[]> {
    return this.apollo.query<GetAllClinicsReponse>({
      query: GetAllClinicsQuery,
      fetchPolicy: 'network-only'
    }).pipe(map(res => res.data.clinics?.items ?? []));
  }

  getUserClinicsWithModality(): Observable<ClinicWithOrthancModalityInterface[]> {
    return this.apollo.query<GetUserClinicsReponse>({
      query: GetUserClinicsWithModalityQuery,
      fetchPolicy: 'network-only'
    }).pipe(map(res => res.data.userClinics));
  }

  getClinicGroups(): Observable<ClinicMinimalInterface[]> {
    return this.apollo.query<GetClinicsMinimalReponse>({
      query: GetClinicGroupsQuery
    }).pipe(map(res => res.data.clinics?.items ?? []));
  }

  getClinicById(clinicId: number): Observable<ClinicFullInterface> {
    return this.apollo.query<GetClinicById>({
      query: GetClinicByIdQuery,
      variables : {
        clinicId
      },
      fetchPolicy: 'network-only'
    }).pipe(map(res => res.data.clinicById));
  }

  addClinic(input: AddOrUpdateDtoInterface): Observable<ClinicWithOrthancModalityInterface | undefined> {
    return this.apollo.mutate<{ addClinic: ClinicWithOrthancModalityInterface }>({
      mutation: gql`mutation adminAddClinic($dto: ClinicAddOrUpdateInput) {
        addClinic(input: $dto) {
          id
          name
          shortName
          parentName
          orthancIp
          orthancPort
          isGroup
          logoUrl
          studyStorageMode
        }
      }`,
      variables: { dto: input },
      context: {
        useMultipart: true
      },
      refetchQueries: [{
        query: this.appStateService.isScc ? GetSccClinicsQuery : GetMyPacsClinicsQuery
      },
      {
        query: GetClinicGroupsQuery
      }]
    }).pipe(map(res =>res.data ? res.data?.addClinic : undefined));
  }

  searchClinics(searchText: string): Observable<ClinicInterface[]> {
    return this.apollo.query<SearchClinicsReponse>({
      query: gql`query adminSearchClinic($searchText: String!) {
        userClinics(
          order: { name: ASC },
          where: { and: [
            { or: [
              { name: { contains: $searchText } },
              { alias: { contains: $searchText } }
            ]
          }]
        }) {
          id
          name
          shortName
          alias
          isGroup
        }
      }`,
      variables: { searchText }
    }).pipe(map(res => res.data.userClinics));
  }

  searchClinicsWithLevenshtein(search: string, zipCode: string): Observable<ClinicInterface[]> {
    return this.apollo.query<{ searchClinics: { object: ClinicInterface; distance: number }[] }>({
      query: gql`query searchClinics($search: String!, $zipCode: String!) {
        searchClinics(search: $search, take: 5, zipCode : $zipCode) {
          object {
            id
            name
            city
            phone
            hasPaymentMethod
          }
          distance
        }
      }`,
      variables: { search, zipCode }
    }).pipe(map(res => res.data.searchClinics.map(wrapper => {
      const clinic = { ...wrapper.object };
      clinic.distance = wrapper.distance;
      return clinic;
    }).filter(clinic => (clinic.distance ?? 1) >= 0.7)) );
  }

  enableMyPacsAccessForClinic(clinicId: number): Observable<ClinicWithOrthancModalityInterface | undefined> {
    return this.apollo.mutate<{ enableMyPacsAccessForClinic: ClinicWithOrthancModalityInterface }>({
      mutation: gql`mutation enableMyPacsAccessForClinic($clinicId: Long!) {
        enableMyPacsAccessForClinic(clinicId: $clinicId) {
          id
          name
          isMyPacs
          hasMyPacsAccess
        }
      }`,
      variables: { clinicId },
      refetchQueries: [{
        query: this.appStateService.isScc ? GetSccClinicsQuery : GetMyPacsClinicsQuery
      }]
    }).pipe(map(res => res.data?.enableMyPacsAccessForClinic));
  }

  public updateClinic(input: AddOrUpdateDtoInterface ): Observable<ClinicWithOrthancModalityInterface | undefined> {
    const clinicsQuery = this.appStateService.isScc ? GetSccClinicsQuery : GetMyPacsClinicsQuery;

    return this.apollo.mutate<{ updateClinic: ClinicWithOrthancModalityInterface }>({
      mutation : gql`mutation adminUpdateClinic($dto: ClinicAddOrUpdateInput) {
        updateClinic(input: $dto) {
          id
          name
          shortName
          parentName
          orthancIp
          orthancPort
          isGroup
          logoUrl
          studyStorageMode
        }
      }`,
      variables: { dto : input },
      refetchQueries: [{
        query: clinicsQuery
      },
      {
        query: GetClinicByIdQuery, variables : {  clinicId : input.clinic.id }
      },
      {
        query: GetClinicGroupsQuery
      }],
      context: {
        useMultipart: true
      },
      update: (store, { data }) => {
        const getClinicsWithModalityQuery = store.readQuery<GetAllClinicsReponse>({ query: clinicsQuery });

        if (getClinicsWithModalityQuery && data) {
          let clinics = getClinicsWithModalityQuery.clinics?.items?.map(c => ({ ...c }) as ClinicWithOrthancModalityInterface);

          if (clinics) {
            clinics = [data.updateClinic, ...clinics.filter(c=> c.id !== input.clinic.id)];
          }

          store.writeQuery<GetAllClinicsReponse>({ query: clinicsQuery, data : { clinics: { items: clinics } }  });
        }
      }
    },
    ).pipe(map(res =>res.data ? res.data?.updateClinic : undefined));
  }
}

export interface GetAllClinicsReponse {
  clinics: { items: ClinicWithOrthancModalityInterface[] };
}

export interface GetUserClinicsReponse {
  userClinics: ClinicWithOrthancModalityInterface[];
}

export interface GetClinicsMinimalReponse {
  clinics: { items: ClinicMinimalInterface[] };
}

export interface AddClinic {
  addClinic: ClinicWithOrthancModalityInterface[];
}

export interface GetClinicById {
  clinicById: ClinicFullInterface;
}

export interface SearchClinicsReponse {
  userClinics: ClinicInterface[];
}

export interface PaymentMethod {
  bankCode?: string;
  brand?: string;
  expMonth?: number;
  expYear?: number;
  isCard?: boolean;
  last4?: string;
  hasMethod?: boolean;
  valid?: boolean;
  stripeCustomerId?: string;
  hasUnpaidInvoices?: boolean;
}
